import { Easings } from "konva/lib/Tween";
import { getNormalizedRect, isShapeIntersecting } from "./canvasGeometryUtils";
import { uploadImageService } from "src/services/uploadImageService";

export const handleMouseDown = (
  e,
  mode,
  setIsDrawing,
  setDrawingShape,
  setSelectedIds,
  addTextToPlan,
  stageRef
) => {
  if (e.evt.button !== 0) return;

  const stage = stageRef.current;
  const pointer = stage.getPointerPosition();
  const transform = stage.getAbsoluteTransform().copy().invert();
  const localPos = transform.point(pointer);

  if (mode === "draw") {
    setIsDrawing(true);
    setDrawingShape({
      x: localPos.x,
      y: localPos.y,
      width: 0,
      height: 0,
      type: "rect",
    });
  } else if (mode === "select") {
    const clickedOnEmpty = e.target === stage;
    if (clickedOnEmpty) {
      setIsDrawing(true);
      setDrawingShape({
        x: localPos.x,
        y: localPos.y,
        width: 0,
        height: 0,
        type: "selection",
      });
    }
  } else {
    const id = Date.now().toString();
    addTextToPlan({
      id,
      x: localPos.x,
      y: localPos.y,
      text: "",
    });
  }
};

export const handleMouseUp = (
  e,
  isDrawing,
  drawingShape,
  setIsDrawing,
  setDrawingShape,
  selectedArea,
  setSelectedIds,
  floorPlans,
  addRectToPlan,
  shapeRefs,
  transformerRef
) => {
  if (!isDrawing || !drawingShape || e.evt.button !== 0) return;

  if (drawingShape.type === "rect") {
    const newShape = { ...drawingShape, id: Date.now().toString() };
    if (Math.abs(newShape.width) < 8 || Math.abs(newShape.height) < 8) {
      setIsDrawing(false);
      setDrawingShape(null);
      return;
    }
    addRectToPlan(newShape);
  }

  if (drawingShape.type === "selection") {
    const selectionBox = getNormalizedRect(drawingShape);
    if (selectionBox.width < 30 || selectionBox.height < 30) {
      setIsDrawing(false);
      setDrawingShape(null);
      return;
    }

    const selected = floorPlans[selectedArea?.id]
      ?.filter(
        (shape) =>
          shape.type !== "image" && shape.type !== "viewport" &&
          isShapeIntersecting(selectionBox, shape, shapeRefs)
      )
      ?.map((s) => s.id);

    if (!selected?.length) {
      setIsDrawing(false);
      setDrawingShape(null);
      return;
    }

    setSelectedIds(selected);
    setTimeout(() => {
      const nodes = selected
        .map((sid) => shapeRefs.current[sid])
        .filter(Boolean);
      if (transformerRef.current && nodes.length > 0) {
        transformerRef.current.nodes(nodes);
        transformerRef.current.getLayer().batchDraw();
      }
    }, 0);
  }

  setIsDrawing(false);
  setDrawingShape(null);
};

export const handleMouseMove = (
  e,
  isDrawing,
  drawingShape,
  setDrawingShape,
  stageRef
) => {
  if (!isDrawing || !drawingShape || e.evt.button !== 0) return;

  const stage = stageRef.current;
  const pointer = stage.getPointerPosition();
  const transform = stage.getAbsoluteTransform().copy().invert();
  const localPos = transform.point(pointer);

  const newWidth = localPos.x - drawingShape.x;
  const newHeight = localPos.y - drawingShape.y;

  setDrawingShape({
    ...drawingShape,
    width: newWidth,
    height: newHeight,
  });
};

export const handleClickTable = (id, selectedIds, setSelectedIds) => {
  if (!selectedIds.find((selectedId) => selectedId === id)) {
    setSelectedIds((prev) => [...prev, id]);
  } else {
    setSelectedIds((prev) => prev.filter((tableId) => tableId !== id));
  }
};

export const handleSelect = (
  e,
  id,
  mode,
  setSelectedIds,
  shapeRefs,
  transformerRef
) => {
  if (mode !== "select") return;
  const isMultiSelect = e?.evt?.ctrlKey || e?.evt?.metaKey;
  setSelectedIds((prevSelectedIds) => {
    let newSelectedIds;

    if (isMultiSelect) {
      if (prevSelectedIds.includes(id)) {
        newSelectedIds = prevSelectedIds.filter((item) => item !== id);
      } else {
        newSelectedIds = [...prevSelectedIds, id];
      }
    } else {
      newSelectedIds = [id];
    }

    setTimeout(() => {
      const nodes = newSelectedIds
        .map((sid) => shapeRefs.current[sid])
        .filter(Boolean);

      if (transformerRef.current && nodes.length > 0) {
        transformerRef.current.nodes(nodes);
        transformerRef.current.getLayer().batchDraw();
      }
    }, 0);

    return newSelectedIds;
  });
};

export const handleDragEnd = (
  e,
  id,
  selectedIds,
  selectedArea,
  floorPlans,
  setFloorPlans,
  shapeRefs
) => {
  const isMultiSelect = selectedIds.length > 1 && selectedIds.includes(id);
  const idsToUpdate = isMultiSelect ? selectedIds : [id];

  const updatedTables = floorPlans[selectedArea?.id].map((table) => {
    if (idsToUpdate.includes(table.id)) {
      const node = shapeRefs.current[table.id];
      return {
        ...table,
        x: node?.x() ?? table.x,
        y: node?.y() ?? table.y,
      };
    }
    return table;
  });

  setFloorPlans((prev) => ({
    ...prev,
    [selectedArea?.id]: updatedTables,
  }));
};

export const handleTransformImgOrViewport = (  
  id,
  floorPlans,
  setFloorPlans,
  setSelectedIds,
  selectedArea,
  shapeRefs,
  transformerRef
) => {
  const node = shapeRefs.current[id];
  if (!node) return;

  const box = node.getClientRect({ relativeTo: node.getParent() });

  const updatedShapes = floorPlans[selectedArea?.id].map((shape) => {
    if (shape.id === id) {
      return {
        ...shape,
        x: node.x(),
        y: node.y(),
        width: Math.max(8, box.width),
        height: Math.max(8, box.height),
      };
    }
    return shape;
  });

  node.scale({ x: 1, y: 1 });
  clearSelectTransform(setSelectedIds, transformerRef)

  setFloorPlans((prev) => ({
    ...prev,
    [selectedArea?.id]: updatedShapes,
  }));
}

export const handleTransform = (
  e,
  id,
  selectedIds,
  floorPlans,
  setFloorPlans,
  selectedArea,
  shapeRefs
) => {
  const isMultiSelect = selectedIds.length > 1 && selectedIds.includes(id);
  const idsToUpdate = isMultiSelect ? selectedIds : [id];

  const updatedShapes = floorPlans[selectedArea?.id].map((shape) => {
    if (idsToUpdate.includes(shape.id)) {
      const node = shapeRefs.current[shape.id];
      const newRotation = Math.round((node?.rotation() ?? 0) / 45) * 45;
      if (node) node.rotation(newRotation);
      console.log("shape", node)
      return {
        ...shape,
        x: node?.x() ?? shape.x,
        y: node?.y() ?? shape.y,
        ...(shape.type !== "viewport" && {rotation: newRotation}),
        ...(selectedIds.length !== 1 ? {}
          : ["rect", "viewport"].includes(shape.type)
          ? {
              width: Math.max(8, node.width() * node.scaleX()),
              height: Math.max(8, node.height() * node.scaleY()),
            }
          : shape.type === "text"
          ? { width: Math.max(40, node.width() * node.scaleX()) }
          : {}),
      };
    }
    return shape;
  });

  if (selectedIds.length === 1 && shapeRefs.current[id]) {
    const node = shapeRefs.current[id];
    node.scale({ x: 1, y: 1 });
  }

  setFloorPlans((prev) => ({
    ...prev,
    [selectedArea?.id]: updatedShapes,
  }));
};

export const clearSelectTransform = (setSelectedIds, transformerRef) => {
  setSelectedIds([]);
  if (transformerRef.current) transformerRef.current.nodes([]);
};

export const handleDeselect = (
  e,
  setSelectedIds,
  transformerRef,
  imageTransformerRef
) => {
  if (e.target === e.target.getStage()) {
    setSelectedIds([]);
    if (transformerRef.current) transformerRef.current.nodes([]);
    if (imageTransformerRef.current) imageTransformerRef.current.nodes([]);
  }
};

export const handleWheel = (e, stageRef) => {
  if (!e.evt.ctrlKey || document.querySelector("textarea")) return;
  e.evt.preventDefault();

  const stage = stageRef.current;
  const oldScale = stage.scaleX();
  const pointer = stage.getPointerPosition();

  const mousePointTo = {
    x: (pointer.x - stage.x()) / oldScale,
    y: (pointer.y - stage.y()) / oldScale,
  };

  let direction = e.evt.deltaY > 0 ? -1 : 1;

  const scaleBy = 1.1;
  const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

  stage.scale({ x: newScale, y: newScale });

  const newPos = {
    x: pointer.x - mousePointTo.x * newScale,
    y: pointer.y - mousePointTo.y * newScale,
  };
  stage.position(newPos);
};

export const handleReCenter = (stageRef) => {
  const stage = stageRef.current;
  stage.to({
    x: 0,
    y: 0,
    scaleX: 1,
    scaleY: 1,
    duration: 0.5,
    easing: Easings.EaseInOut,
  });
};

const handleZoom = (stageRef, direction) => {
  const stage = stageRef.current;
  const oldScale = stage.scaleX();
  const scaleBy = 1.1;
  const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
  stage.scale({ x: newScale, y: newScale });
};

export const handleZoomOut = (stageRef) => {
  handleZoom(stageRef, -1);
};

export const handleZoomIn = (stageRef) => {
  handleZoom(stageRef, 1);
};

export const handleFileChange = async (
  e,
  selectedArea,
  setCustomerPlans,
  enqueueSnackbar,
  uploadMedia,
  setMediaPublic
) => {
  const file = e.target.files[0];
  if (!file) return;

  const imageUrl = await uploadImageService(
    file,
    enqueueSnackbar,
    uploadMedia,
    setMediaPublic,
    false,
    true
  );

  if (!imageUrl) return;

  const img = new window.Image();
  img.onload = () => {
    const aspectRatio = img.width / img.height;
    const calculatedWidth = 400 * aspectRatio;

    setCustomerPlans((prevPlans) => ({
      ...prevPlans,
      [selectedArea.id]: [
        ...(prevPlans[selectedArea.id] || []),
        {
          id: `image-${Date.now()}`,
          type: "image",
          src: imageUrl,
          x: 100,
          y: 100,
          width: calculatedWidth,
          height: 400,
        },
      ],
    }));
  };
  img.src = imageUrl;
};
